import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Menu, X, Phone, Mail, MapPin } from 'lucide-react';
import { SERVICE_AREAS } from '../serviceAreas';
import { SERVICE_PAGES } from '../servicePages';

// Sets the document title and meta description for a page (SPA per-route SEO)
export const usePageMeta = (title, description) => {
  useEffect(() => {
    document.title = title;
    if (description) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) meta.setAttribute('content', description);
    }
  }, [title, description]);
};

// Shared Header Component
export const Header = ({ currentPage = 'home' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { href: '/', label: 'Home', id: 'home' },
    { href: '/services', label: 'Services', id: 'services' },
    { href: '/about', label: 'About', id: 'about' },
    { href: '/contact', label: 'Contact', id: 'contact' },
    { href: '/links', label: 'Follow Us', id: 'links' }
  ];

  const handleQuoteClick = useCallback(() => {
    // Try to find quote form first
    const quoteForm = document.getElementById('quote-form');
    if (quoteForm) {
      quoteForm.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // Fallback to any element with space-y-6 class
    const fallbackElement = document.querySelector('.space-y-6');
    if (fallbackElement) {
      fallbackElement.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    
    // If neither exists, trigger modal event
    window.dispatchEvent(new CustomEvent('openQuoteModal'));
  }, []);

  return (
    <header className={`fixed w-full z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-md' : 'bg-white/95 backdrop-blur'
    }`}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center h-16">
          <a href="/" className="flex items-center gap-2 sm:gap-3 flex-shrink-0 hover:opacity-80 transition-opacity duration-200">
            <span className="font-display leading-none text-gray-900 whitespace-nowrap">
              <span className="block text-2xl tracking-wide">ONE CHOICE</span>
              <span className="block text-xs tracking-[0.22em] text-gray-500 mt-0.5">
                TREE SERVICE LLC
              </span>
            </span>
            <img
              src="/logo-mark.svg"
              alt="One Choice Tree Service"
              className="h-14 sm:h-[3.4rem] w-auto flex-shrink-0"
            />
          </a>

          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map(item => (
              <a 
                key={item.id}
                href={item.href} 
                className={`font-medium ${
                  currentPage === item.id
                    ? 'text-green-600'
                    : 'text-gray-600 hover:text-green-600'
                }`}
              >
                {item.label}
              </a>
            ))}
            
            <button
              className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-2 rounded-lg font-semibold"
              onClick={handleQuoteClick}
            >
              Get Free Quote
            </button>
          </div>

          <button
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu — full-screen overlay below the header */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 h-[calc(100vh-4rem)] bg-white border-t border-gray-200 overflow-y-auto">
            <nav className="flex flex-col items-center gap-8 px-6 pt-8 pb-10">
              {navItems.map(item => (
                <a
                  key={item.id}
                  href={item.href}
                  className={`text-2xl font-semibold ${
                    currentPage === item.id
                      ? 'text-green-600'
                      : 'text-gray-800 hover:text-green-600'
                  }`}
                >
                  {item.label}
                </a>
              ))}
              <button
                className="w-full max-w-xs bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-4 rounded-xl text-xl font-semibold"
                onClick={() => {
                  setIsMenuOpen(false);
                  handleQuoteClick();
                }}
              >
                Get Free Quote
              </button>

              {/* Swipeable service cards */}
              <div className="w-full">
                <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 text-center mb-3">
                  OUR SERVICES
                </p>
                <div
                  className="flex overflow-x-auto gap-4 pb-2 snap-x snap-mandatory"
                  style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                  {[
                    {
                      title: 'Tree Removal',
                      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/2893782948520664233.jpg'
                    },
                    {
                      title: 'Tree Trimming',
                      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg'
                    },
                    {
                      title: 'Storm Damage',
                      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/9167796078228255619.jpg'
                    },
                    {
                      title: 'Precision Work',
                      image: 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/5584559015441101802.jpg'
                    }
                  ].map((service) => (
                    <a
                      key={service.title}
                      href="/services"
                      className="relative flex-shrink-0 w-56 h-36 snap-center rounded-xl overflow-hidden shadow-md"
                    >
                      <img
                        src={service.image}
                        alt={service.title}
                        loading="lazy"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <span className="absolute bottom-3 left-4 text-white font-semibold">
                        {service.title}
                      </span>
                    </a>
                  ))}
                </div>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Shared Footer Component (includes the sticky mobile call/quote bar)
export const Footer = () => {
  // Keep the sticky bar hidden until the hero CTAs have scrolled out of view,
  // so the same buttons never show twice on screen at once.
  const [showMobileBar, setShowMobileBar] = useState(false);
  // After 20s on the page, nudge undecided visitors toward the form
  const [showNudge, setShowNudge] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowMobileBar(window.scrollY > window.innerHeight * 0.7);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowNudge(true), 20000);
    return () => clearTimeout(timer);
  }, []);

  // Report the nudge to GA4/Clarity the first time it's actually on screen
  // (timer fired AND the bar is visible on a mobile-width viewport)
  const nudgeTracked = useRef(false);
  useEffect(() => {
    if (!showNudge || !showMobileBar || nudgeTracked.current) return;
    if (window.innerWidth >= 1024) return; // bar is hidden on desktop
    nudgeTracked.current = true;
    if (window.gtag) {
      window.gtag('event', 'cta_nudge_shown', {
        event_category: 'engagement',
        event_label: 'mobile_cta_24h_reply_nudge'
      });
    }
    if (window.clarity) {
      window.clarity('event', 'cta_nudge_shown');
    }
  }, [showNudge, showMobileBar]);

  const handleMobileQuote = () => {
    const quoteForm = document.getElementById('quote-form');
    if (quoteForm) {
      quoteForm.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    window.dispatchEvent(new CustomEvent('openQuoteModal'));
  };

  return (
    <>
    <footer className="relative overflow-hidden bg-gray-900 text-white py-12 pb-28 lg:pb-12">
      {/* Ghosted brand watermark — decorative, sits behind the footer content */}
      <div
        aria-hidden="true"
        className="pointer-events-none select-none absolute inset-x-0 bottom-0 flex items-end justify-center gap-[0.12em] translate-y-[32%] text-[17vw] leading-none"
      >
        <img src="/logo-mark-white.svg" alt="" className="h-[1.05em] w-auto opacity-[0.05]" />
        <span className="font-display whitespace-nowrap tracking-wide text-white/[0.05]">
          ONE CHOICE
        </span>
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="font-display leading-none text-white">
                <span className="block text-2xl tracking-wide">ONE CHOICE</span>
                <span className="block text-xs tracking-[0.22em] text-gray-400 mt-1">
                  TREE SERVICE LLC
                </span>
              </span>
              <img
                src="/logo-mark-white.svg"
                alt="One Choice Tree Service"
                className="h-14 w-auto flex-shrink-0"
              />
            </div>
            <p className="text-gray-400">
              Professional tree care in Maryland. Licensed, insured, and trusted by hundreds of homeowners.
            </p>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Contact</h3>
            <div className="space-y-2 text-gray-400">
              <div className="flex items-center space-x-2">
                <MapPin className="w-4 h-4 flex-shrink-0" />
                <a 
                  href="https://maps.google.com/?q=105+Wallace+Ave,+Brooklyn,+MD+21225"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white transition-colors"
                >
                  105 Wallace Ave, Brooklyn, MD 21225
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a 
                  href="tel:+14432080747"
                  className="hover:text-white transition-colors"
                >
                  (443) 208-0747
                </a>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a 
                  href="mailto:contact@onechoicetreeservice.com"
                  className="hover:text-white transition-colors"
                >
                  contact@onechoicetreeservice.com
                </a>
              </div>
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Hours</h3>
            <div className="text-gray-400 text-sm">
              <p>Mon-Fri: 7AM - 6PM</p>
              <p>Saturday: 8AM - 4PM</p>
              <p>Sunday: Emergency Only</p>
              <p className="text-green-400 mt-2">24/7 Emergency Service</p>
            </div>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <div className="space-y-2 text-gray-400 text-sm">
              <p>
                <a href="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </a>
              </p>
              <p>
                <a href="/terms" className="hover:text-white transition-colors">
                  Terms of Use
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Service landing pages — sitewide links so they aren't orphaned */}
        <div className="border-t border-gray-800/60 pt-8 mb-8">
          <h3 className="font-semibold mb-4">Services</h3>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-400">
            {SERVICE_PAGES.map(page => (
              <a
                key={page.slug}
                href={`/${page.slug}`}
                className="hover:text-white transition-colors"
              >
                {page.name}
              </a>
            ))}
            <a href="/services" className="hover:text-white transition-colors">
              All Services
            </a>
          </div>
        </div>

        {/* Town pages — sitewide links so they aren't orphaned */}
        <div className="border-t border-gray-800/60 pt-8 mb-8">
          <h3 className="font-semibold mb-4">Service Areas</h3>
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-400">
            {SERVICE_AREAS.map(area => (
              <a
                key={area.slug}
                href={`/tree-service/${area.slug}`}
                className="hover:text-white transition-colors"
              >
                Tree Service {area.name}
              </a>
            ))}
          </div>
        </div>

        <div className="pt-8">
          <p className="text-gray-400 text-center sm:text-left">
            © 2026 ONE CHOICE TREE SERVICE LLC. Licensed Maryland contractor.
          </p>
        </div>
      </div>
    </footer>

    {/* Sticky mobile CTA bar — fades in once the hero CTAs scroll away.
        Deliberately fades rather than slides: iOS Safari mis-composites a
        position:fixed element that carries a transform, stranding it partway
        up the screen instead of pinned to the bottom. No transform here, and
        padding clears the home indicator. */}
    <div
      className={`fixed bottom-0 inset-x-0 z-40 lg:hidden bg-white border-t border-gray-200 shadow-[0_-2px_10px_rgba(0,0,0,0.1)] transition-opacity duration-300 ${
        showMobileBar ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {showNudge && (
        <p className="text-center text-xs font-medium text-green-800 bg-green-50 border-b border-green-100 px-3 py-1.5">
          Submit a request — we'll reply within 24 hours
        </p>
      )}
      <div className="grid grid-cols-2 gap-2 p-2">
        <a
          href="tel:+14432080747"
          className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 active:from-green-600 active:to-emerald-700 text-white py-3 rounded-lg font-semibold"
        >
          <Phone className="w-4 h-4" />
          <span>Call Now</span>
        </a>
        <button
          onClick={handleMobileQuote}
          className="border-2 border-green-600 text-green-600 active:bg-green-50 py-3 rounded-lg font-semibold"
        >
          Free Quote
        </button>
      </div>
    </div>
    </>
  );
};
const SERVICE_OPTIONS = [
  'Tree Removal',
  'Tree Trimming & Pruning',
  'Stump Grinding',
  'Storm Damage / Emergency',
  'Tree Health & Care',
  'Crane Services',
  'Lot Clearing',
  'Commercial Services',
  'Residential Services',
  'Other'
];

// Quote Form Modal — one simple form, nothing fancy
// Photo shown at the top of the quote form for the selected service. Keyed by
// SERVICE_OPTIONS value; real One Choice job photos where we have one for the
// job type, otherwise the same Pexels shot that service's card uses. Crane
// deliberately uses our own lift photo rather than the card's sandhills.com
// hotlink. 'Other' has no image — the form just opens without a banner.
const SERVICE_IMAGES = {
  'Tree Removal': 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/2893782948520664233.jpg',
  'Tree Trimming & Pruning': 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/6794577484929289205.jpg',
  'Storm Damage / Emergency': 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/9167796078228255619.jpg',
  'Crane Services': 'https://flappy-bird.nyc3.cdn.digitaloceanspaces.com/ONE/5584559015441101802.jpg',
  'Stump Grinding': 'https://images.pexels.com/photos/31127615/pexels-photo-31127615/free-photo-of-arborist-cutting-down-tree-trunk-safely-with-chainsaw.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
  'Tree Health & Care': 'https://images.pexels.com/photos/5215651/pexels-photo-5215651.jpeg?auto=compress&cs=tinysrgb&w=800',
  'Lot Clearing': 'https://images.pexels.com/photos/18060323/pexels-photo-18060323.jpeg?auto=compress&cs=tinysrgb&w=800',
  'Commercial Services': 'https://images.pexels.com/photos/269077/pexels-photo-269077.jpeg?auto=compress&cs=tinysrgb&w=800',
  'Residential Services': 'https://images.pexels.com/photos/17208219/pexels-photo-17208219.jpeg?auto=compress&cs=tinysrgb&w=800'
};

const QuoteModal = ({ isOpen, onClose, prefillService = '' }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'success' | 'error' | 'validation'
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    service: '',
    description: ''
  });

  // Preselect the service when opened from a service card
  useEffect(() => {
    if (!isOpen || !prefillService) return;
    if (SERVICE_OPTIONS.includes(prefillService)) {
      setFormData(prev => ({ ...prev, service: prefillService }));
    } else {
      setFormData(prev => ({ ...prev, service: 'Other', description: prev.description || prefillService }));
    }
  }, [isOpen, prefillService]);

  const handleInputChange = useCallback((field, value) => {
    setSubmitStatus(prev => (prev === 'validation' ? null : prev));
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const resetForm = () => {
    setFormData({ name: '', phone: '', email: '', address: '', service: '', description: '' });
    setSubmitStatus(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name.trim() ||
      !formData.phone.trim() ||
      !formData.service ||
      (formData.service === 'Other' && !formData.description.trim())
    ) {
      setSubmitStatus('validation');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_key: '80c96390-50a6-4c0a-b38a-a671e4b7f92b',
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          service: formData.service,
          message: formData.description,
          subject: 'New Tree Service Quote Request'
        })
      });

      if (response.ok) {
        // Google Ads conversion tracking (Website lead)
        if (window.gtag) {
          window.gtag('event', 'conversion', { send_to: 'AW-18371423166/5g94CJ_bodwcEL7XlrhE' });
        }

        setSubmitStatus('success');
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputClass = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-600 focus:border-transparent";
  const serviceImage = SERVICE_IMAGES[formData.service];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={handleClose}
        ></div>

        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full mx-auto p-6 text-left transform transition-all">
          {/* Close button — gets a scrim when it sits over the photo banner */}
          <button
            onClick={handleClose}
            className={`absolute top-4 right-4 z-10 rounded-full transition-colors ${
              serviceImage && submitStatus !== 'success'
                ? 'bg-black/40 p-1.5 text-white hover:bg-black/60'
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>

          {submitStatus === 'success' ? (
            /* What happens next — replaces the form after submission */
            <div className="py-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">What happens next?</h2>
              <p className="text-gray-600 mb-4">
                We'll contact you within 24 hours to schedule your free on-site assessment.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Need us sooner? Call{' '}
                <a href="tel:+14432080747" className="text-green-600 font-semibold">(443) 208-0747</a>
              </p>
              <button
                onClick={handleClose}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-8 py-3 rounded-lg font-semibold"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Photo banner for the chosen service — bleeds to the modal edges
                  and swaps live as the dropdown changes */}
              {serviceImage && (
                <div className="relative -mx-6 -mt-6 mb-5 h-36 overflow-hidden rounded-t-xl">
                  <img
                    src={serviceImage}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10"></div>
                  <div className="absolute inset-x-0 bottom-0 px-5 pb-4">
                    <p className="text-[0.6rem] font-semibold tracking-[0.22em] text-green-300 mb-1">
                      FREE ESTIMATE
                    </p>
                    <p className="font-display text-2xl leading-none text-white">
                      {formData.service}
                    </p>
                  </div>
                </div>
              )}

              <div className="text-center mb-6">
                {!serviceImage && (
                  <h2 className="text-xl font-bold text-gray-900 mb-2">Get Your Free Quote</h2>
                )}
                <p className="text-sm text-gray-600">Licensed • Insured • 100% Free Estimates</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={inputClass}
                  placeholder="Name *"
                  autoComplete="name"
                />
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={inputClass}
                  placeholder="Phone *"
                  autoComplete="tel"
                />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className={inputClass}
                  placeholder="Email"
                  autoComplete="email"
                />
                <select
                  value={formData.service}
                  onChange={(e) => handleInputChange('service', e.target.value)}
                  className={`${inputClass} bg-white ${formData.service ? 'text-gray-900' : 'text-gray-400'}`}
                >
                  <option value="" disabled>What do you need? *</option>
                  {SERVICE_OPTIONS.map((option) => (
                    <option key={option} value={option} className="text-gray-900">
                      {option}
                    </option>
                  ))}
                </select>

                {formData.service && (
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => handleInputChange('address', e.target.value)}
                    className={inputClass}
                    placeholder="Property address (optional)"
                    autoComplete="street-address"
                  />
                )}

                {formData.service === 'Other' && (
                  <>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className={inputClass}
                      rows="3"
                      placeholder="Tell us what you need *"
                    />
                    <p className="text-xs text-gray-500">
                      Please, no solicitation emails — this form is for tree service requests only.
                    </p>
                  </>
                )}

                {formData.service && formData.service !== 'Other' && (
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className={inputClass}
                    rows="3"
                    placeholder="Anything else we should know? (optional)"
                  />
                )}

                {submitStatus === 'validation' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    Please enter your name and phone number, and select what you need done.
                  </div>
                )}
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                    We couldn't send your request. Please try again, or call us at{' '}
                    <a href="tel:+14432080747" className="font-semibold underline">(443) 208-0747</a>.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-3 rounded-lg font-semibold disabled:opacity-50"
                >
                  {isSubmitting ? 'Sending...' : 'Send Quote Request'}
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-gray-200"></div>
                  <span className="text-xs text-gray-400 uppercase tracking-wide">or</span>
                  <div className="flex-1 h-px bg-gray-200"></div>
                </div>

                <a
                  href="tel:+14432080747"
                  className="w-full flex items-center justify-center space-x-2 border-2 border-green-600 text-green-600 hover:bg-green-50 py-3 rounded-lg font-semibold"
                >
                  <Phone className="w-4 h-4" />
                  <span>Call (443) 208-0747</span>
                </a>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Hook for quote modal functionality
export const useQuoteModal = () => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const [prefillService, setPrefillService] = useState('');

  useEffect(() => {
    const handleOpenModal = (event) => {
      setPrefillService(event?.detail?.service || '');
      setIsQuoteModalOpen(true);
    };
    window.addEventListener('openQuoteModal', handleOpenModal);
    return () => window.removeEventListener('openQuoteModal', handleOpenModal);
  }, []);

  // Accepts an optional service name; ignores React event objects from onClick
  const openQuoteModal = useCallback((service) => {
    setPrefillService(typeof service === 'string' ? service : '');
    setIsQuoteModalOpen(true);
  }, []);
  const closeQuoteModal = useCallback(() => setIsQuoteModalOpen(false), []);

  const QuoteModalComponent = useMemo(() =>
    () => <QuoteModal isOpen={isQuoteModalOpen} onClose={closeQuoteModal} prefillService={prefillService} />,
    [isQuoteModalOpen, closeQuoteModal, prefillService]
  );

  return {
    isQuoteModalOpen,
    openQuoteModal,
    closeQuoteModal,
    QuoteModal: QuoteModalComponent
  };
};

